import { endpoints } from "./endpoints"
import { authFetch } from "../utils/auth-fetch"

export type Role = "admin" | "learner"
export interface Membership {
  organizationId: number
  organizationName: string
  roles: Role[]
  employeeId: string | null
}
export interface Account extends Membership {
  accountId: string
  email: string
  isAdmin: boolean
  isSuperAdmin: boolean
  ssoEnabled: boolean
  permissions: string[]
  memberships: Membership[]
}
export interface Learning {
  employee: {
    userId: string
    employeeId: string | null
    firstName: string | null
    lastName: string | null
    organizationName: string
  }
  certifications: {
    id: number
    courseTitle: string
    provider: string
    completionDate: string
    expirationDate: string | null
    completionStatus: string | null
    score: number | null
  }[]
}
export interface LearnerAccess {
  membership: { id: string; email: string; isActive: boolean } | null
  pendingInvitation: { id: string; email: string; expiresAt: string } | null
}
export class AccountApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}
export async function accountRequest(url: string, init?: RequestInit): Promise<unknown> {
  const response = await authFetch(url, init)
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new AccountApiError(
      response.status,
      typeof body?.error === "string" ? body.error : "Request failed. Please try again.",
    )
  }
  return response.status === 204 ? undefined : response.json()
}
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Invalid account response")
  return value as Record<string, unknown>
}
function string(value: unknown): string {
  if (typeof value !== "string") throw new Error("Invalid account response")
  return value
}
function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error("Invalid account response")
  return value
}
function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") throw new Error("Invalid account response")
  return value
}
function nullableString(value: unknown) {
  return value === null ? null : string(value)
}
function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw new Error("Invalid account response")
  return value
}
function membership(value: unknown): Membership {
  const v = object(value)
  const roles = array(v.roles).map((role): Role => {
    if (role !== "admin" && role !== "learner") throw new Error("Invalid account role")
    return role
  })
  return {
    organizationId: number(v.organizationId),
    organizationName: string(v.organizationName),
    roles,
    employeeId: nullableString(v.employeeId),
  }
}
export async function fetchAccount(): Promise<Account> {
  const v = object(await accountRequest(endpoints.account.me))
  const result = {
    ...membership(v),
    accountId: string(v.accountId),
    email: string(v.email),
    isAdmin: boolean(v.isAdmin),
    isSuperAdmin: boolean(v.isSuperAdmin),
    ssoEnabled: boolean(v.ssoEnabled),
    permissions: array(v.permissions).map(string),
    memberships: array(v.memberships).map(membership),
  }
  if (result.isAdmin !== result.roles.includes("admin") || !result.roles.length)
    throw new Error("Invalid account roles")
  return result
}
export async function fetchLearning(): Promise<Learning> {
  const v = object(await accountRequest(endpoints.account.learning))
  const e = object(v.employee)
  return {
    employee: {
      userId: string(e.userId),
      employeeId: nullableString(e.employeeId),
      firstName: nullableString(e.firstName),
      lastName: nullableString(e.lastName),
      organizationName: string(e.organizationName),
    },
    certifications: array(v.certifications).map((value) => {
      const c = object(value)
      return {
        id: number(c.id),
        courseTitle: string(c.courseTitle),
        provider: string(c.provider),
        completionDate: string(c.completionDate),
        expirationDate: nullableString(c.expirationDate),
        completionStatus: nullableString(c.completionStatus),
        score: c.score === null ? null : number(c.score),
      }
    }),
  }
}
export async function fetchLearnerAccess(employeeId: string): Promise<LearnerAccess> {
  const v = object(
    await accountRequest(`${endpoints.account.access}/${encodeURIComponent(employeeId)}`),
  )
  const m = v.membership === null ? null : object(v.membership)
  const i = v.pendingInvitation === null ? null : object(v.pendingInvitation)
  return {
    membership: m && { id: string(m.id), email: string(m.email), isActive: boolean(m.isActive) },
    pendingInvitation: i && {
      id: string(i.id),
      email: string(i.email),
      expiresAt: string(i.expiresAt),
    },
  }
}
const post = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
})
export async function inviteLearner(employeeId: string, email: string) {
  const v = object(await accountRequest(endpoints.account.invitations, post({ employeeId, email })))
  return {
    invitationId: string(v.invitationId),
    token: string(v.token),
    expiresAt: string(v.expiresAt),
  }
}
export async function acceptLearnerInvitation(token: string) {
  const v = object(await accountRequest(`${endpoints.account.invitations}/accept`, post({ token })))
  return { organizationId: number(v.organizationId) }
}
export const revokeInvitation = (id: string) =>
  accountRequest(`${endpoints.account.invitations}/${encodeURIComponent(id)}`, { method: "DELETE" })
export const revokeLearnerAccess = (employeeId: string) =>
  accountRequest(`${endpoints.account.access}/${encodeURIComponent(employeeId)}`, {
    method: "DELETE",
  })
